import { useId, useMemo, useState } from 'react';
import { foldToOther, geometryRatio, MARK, seriesVar } from './chartTokens';
import { percent, shares } from '../../lib/format';
import './charts.css';

/**
 * Ownership by stakeholder.
 *
 * Form choice: identity + part-to-whole over a handful of entities, where the
 * headline ("who holds the most, and roughly how much") matters more than
 * precise comparison between the 4th and 5th slice. A donut is defensible here
 * and is what people expect on a cap table; the adjacent table carries the
 * precise figures, which is also the relief the light-mode palette requires
 * (three slots sit under 3:1 against the surface).
 *
 * Segments are separated by a 2px surface gap so adjacent hues never touch —
 * that gap is the secondary encoding the CVD floor band relies on.
 */
export default function OwnershipDonut({ stakeholders, total, basis = 'fullyDiluted', size = 240 }) {
  const titleId = useId();
  const [hovered, setHovered] = useState(null);

  const { kept, other } = useMemo(
    () =>
      foldToOther(stakeholders ?? [], {
        limit: 8,
        valueOf: (s) => s[basis],
        labelOf: (s) => s.name,
      }),
    [stakeholders, basis]
  );

  const segments = useMemo(() => {
    const list = kept.map((s, index) => ({
      key: s.stakeholderId,
      label: s.name,
      value: s[basis],
      percent: s.percentFullyDiluted,
      color: seriesVar(index),
    }));
    if (other) {
      list.push({
        key: '__other',
        label: other.label,
        value: other.value,
        percent: null,
        color: 'var(--series-other)',
        members: other.members,
      });
    }
    return list;
  }, [kept, other, basis]);

  if (!segments.length || !total || total === '0') {
    return (
      <div className="chart-empty" style={{ minHeight: size }}>
        No holdings to chart yet.
      </div>
    );
  }

  const stroke = 26;
  const radius = (size - stroke) / 2;
  const centre = size / 2;
  const circumference = 2 * Math.PI * radius;

  let offset = 0;
  const arcs = segments.map((segment) => {
    const ratio = geometryRatio(segment.value, total);
    const length = ratio * circumference;
    // Hold back the surface gap from each arc so neighbours never abut.
    const drawn = Math.max(length - MARK.surfaceGap, 0.5);
    const arc = { ...segment, ratio, dash: drawn, gap: circumference - drawn, offset };
    offset += length;
    return arc;
  });

  const active = hovered ? arcs.find((a) => a.key === hovered) : null;

  return (
    <div className="donut-wrap">
      <svg
        className="donut"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-labelledby={titleId}
      >
        <title id={titleId}>Ownership by stakeholder, fully diluted</title>
        <g transform={`rotate(-90 ${centre} ${centre})`}>
          {arcs.map((arc) => (
            <circle
              key={arc.key}
              cx={centre}
              cy={centre}
              r={radius}
              fill="none"
              stroke={arc.color}
              strokeWidth={hovered === arc.key ? stroke + 4 : stroke}
              strokeDasharray={`${arc.dash} ${arc.gap}`}
              strokeDashoffset={-arc.offset}
              strokeLinecap="butt"
              onMouseEnter={() => setHovered(arc.key)}
              onMouseLeave={() => setHovered(null)}
              style={{ transition: 'stroke-width 90ms ease-out', cursor: 'pointer' }}
            />
          ))}
        </g>

        {/* Centre reads the hovered slice, or the total at rest. Text wears text
            tokens, never the series colour. */}
        <text x={centre} y={centre - 6} className="donut-centre-value" textAnchor="middle">
          {active ? percent(active.percent ?? '0') : shares(total)}
        </text>
        <text x={centre} y={centre + 14} className="donut-centre-label" textAnchor="middle">
          {active ? truncate(active.label, 18) : 'fully diluted'}
        </text>
      </svg>

      {/* Legend is always present for ≥2 series, and doubles as the direct-label
          relief for the low-contrast light-mode slots. */}
      <ul className="donut-legend">
        {arcs.map((arc) => (
          <li
            key={arc.key}
            className={hovered === arc.key ? 'is-active' : undefined}
            onMouseEnter={() => setHovered(arc.key)}
            onMouseLeave={() => setHovered(null)}
          >
            <span className="swatch" style={{ background: arc.color }} aria-hidden="true" />
            <span className="legend-label" title={arc.members ? arc.members.join(', ') : arc.label}>
              {truncate(arc.label, 22)}
            </span>
            <span className="legend-value num">
              {arc.percent ? percent(arc.percent) : `${(arc.ratio * 100).toFixed(2)}%`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function truncate(text, max) {
  const value = String(text ?? '');
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}
